// api/_utils/leanRevalidation.js
//
// Release 2 (Fenced Customization Bundle V1.1) — battle-creation lean
// revalidation (spec Phase 1 item 7 / changelog #17). PURE: called with the
// agent's at-rest standingLeans + the archetype about to be snapshotted;
// returns what may enter the snapshot and what must be omitted, with reasons.
//
// FAIL CLOSED — never trust future UI (spec changelog #17): a lean that is
// not in the to-be-snapshotted archetype's menu, or whose pinned version is
// no longer the live canonicalTextVersion, or that is malformed, is OMITTED
// from the snapshot and reported (the Phase-2 caller writes the durable
// status record + event). Lean DATA on the agent doc is never mutated here —
// leans are durable desired state (an archetype switch-back revalidates them
// right back in).
//
// Consumers:
//   - Phase-2 fenced site 1 (createAgentBattle): valid[] becomes
//     agentContext.standingLeans (id + version + RESOLVED CURRENT text —
//     master spec §3.1 snapshot shape).
//   - change-archetype.js lean-invalidation rider: invalidated[] under the
//     NEW archetype rides the existing rescan event.

import { isValidAdjustmentId, getCanonicalText, getCanonicalTextVersion, findEquipConflicts } from '../../src/data/archetypeAdjustments.js';
import { MASTERY_ENFORCEMENT_ENABLED } from './masteryConfig.js';

// Master spec §3.1 — the domain cap lives here (the validity kernel), and the
// equip endpoint imports it, so the write path and the snapshot path can
// never disagree on the limit. Mastery P2: this is the BASELINE (= mastery
// L1's two slots); level-derived caps are grants above it, resolved by
// resolveLeanCap below.
export const STANDING_LEANS_CAP = 2;

// The structural maximum any chokepoint can ever grant (§6: L6 → 4 slots).
export const MASTERY_LEAN_CAP_MAX = 4;

/**
 * The closed injection vocabulary for the explicit `leanCap` param (end-of-
 * branch ruling M6): every legal cap is an integer in
 * [STANDING_LEANS_CAP .. MASTERY_LEAN_CAP_MAX] — exactly the §6 unlock
 * table's range. An injected value outside it is a caller bug (the §8
 * corrections applier can only ever inject reduced-but-legal entitlements),
 * so it is REJECTED: the pass falls back to the BASELINE cap (fail-closed —
 * a malformed injection must never grant above baseline; omission + record
 * semantics make an over-tight clamp recoverable, an over-wide one isn't)
 * and the attempt is logged loudly.
 */
export function isLegalLeanCap(leanCap) {
  return Number.isInteger(leanCap)
    && leanCap >= STANDING_LEANS_CAP
    && leanCap <= MASTERY_LEAN_CAP_MAX;
}

/**
 * Mastery P2 (spec §6 D1 dual anchor — the SNAPSHOT/kernel half), P2-review
 * redesign: under enforcement the kernel clamps at the STRUCTURAL max (4)
 * on EVERY path; per-user ENTITLEMENT enforcement lives exclusively at the
 * equip-lean chokepoint (live-profile read at write time), so at-rest lean
 * sets are already entitlement-bounded when they reach the kernel.
 *
 * Why the structural max and not a per-user number here: the kernel is
 * consumed by call sites that cannot pass per-user data — the FENCED
 * deploy-prompt path (agentPromptAssembly.js), the client display authority
 * (src/data/characterState.js), and the change-archetype rider all call
 * with the legacy two-field shape. Any per-user cap channel (a stamped
 * field was tried and reviewed out: unstampable at those sites, forgeable
 * via the un-allowlisted agents CREATE rule, stale across archetype
 * switches, and a §8 foreclosure) would make those paths disagree with the
 * battle snapshot — the exact §9/Release-2 single-authority drift this
 * kernel exists to prevent. With the default below, EVERY caller resolves
 * the same cap with zero call-site changes.
 *
 * §8 stays open by design: the explicit `leanCap` param on
 * revalidateStandingLeans is the corrections-phase injection channel — the
 * future correction applier passes the reduced entitlement explicitly
 * (with user notice, per §8) rather than this default. ⚠ The §8 clamp
 * pass is TWO-CHANNEL by ruling Q7: whoever injects a reduced leanCap here
 * MUST run masteryEnforcement.revalidateTempoDial in the SAME pass —
 * dials and leans re-validate together, never leans alone.
 *
 * Dark (enforcement off): baseline — byte-identical to today.
 */
export function resolveLeanCap(enforcementEnabled = MASTERY_ENFORCEMENT_ENABLED) {
  return enforcementEnabled === true ? MASTERY_LEAN_CAP_MAX : STANDING_LEANS_CAP;
}

/**
 * The kernel-ACCEPTED at-rest set for one archetype (end-of-branch ruling
 * M5's counting rule, exported so its consumers never encode "no
 * entitlement clamp" as a leanCap injection — the M6 vocabulary polices
 * that channel for the §8 corrections applier, and a counting call must be
 * distinguishable from an entitlement injection). Runs the full shared
 * pass (per-pin validity + dedupe + conflict groups) clamped only at the
 * STRUCTURAL max: membership is a validity question; entitlement is the
 * caller's check. Consumers: equip-lean's capacity/conflict accounting,
 * the pre-flip census.
 */
export function acceptedStandingLeans({ standingLeans, archetypeCodeId } = {}) {
  return revalidateStandingLeans({
    standingLeans,
    archetypeCodeId,
    leanCap: MASTERY_LEAN_CAP_MAX,
  }).valid;
}

export const LEAN_INVALIDATION_REASONS = Object.freeze({
  MALFORMED: 'malformed',
  NOT_IN_MENU: 'not_in_menu',
  DEPRECATED_VERSION: 'deprecated_version',
  // At-rest set violations (re-asserted at snapshot time; see
  // revalidateStandingLeans header): a conflict group adjudicated AFTER two
  // leans were legally equipped, or a cap tightened after the fact.
  CONFLICTING_LEAN: 'conflicting_lean',
  OVER_CAP: 'over_cap',
  // A same-id pin appearing twice at rest (unreachable via equip-lean, which
  // replaces same-id — but standingLeans is owner-writable via the client
  // SDK, and a duplicate would render one sentence twice at double emphasis
  // AND eat a cap slot; /code-review Phase-5). First occurrence wins.
  DUPLICATE_PIN: 'duplicate_pin',
});

// The invalidated record is bounded (/code-review, Phase-2): agent.standingLeans
// is owner-writable via the client SDK, so a garbage pin's raw strings must
// never flow uncapped into the battle doc (a few 100 KB ids could push
// createAgentBattle past Firestore's 1 MiB doc limit and 500 every deploy —
// the additive keys must NEVER be able to break battle creation). Valid pins
// need no cap: they passed isValidAdjustmentId, so their ids are canonical.
const MAX_INVALIDATED_ID_CHARS = 32;
const MAX_INVALIDATED_RECORDS = 20;
const boundId = (id) => (typeof id === 'string' ? id.slice(0, MAX_INVALIDATED_ID_CHARS) : null);
const boundInvalidated = (records) =>
  records.length <= MAX_INVALIDATED_RECORDS
    ? records
    : [
        ...records.slice(0, MAX_INVALIDATED_RECORDS),
        { adjustmentId: null, version: null, reason: LEAN_INVALIDATION_REASONS.MALFORMED, truncatedCount: records.length - MAX_INVALIDATED_RECORDS },
      ];

/**
 * THE single per-pin validity rule (menu membership + version currency),
 * shared by the equip write path (api/agent/equip-lean.js maps reasons onto
 * its HTTP sentinels) and the snapshot revalidation below — one authority,
 * so equip can never accept a pin revalidation would omit, or vice versa.
 *
 * @returns {{ok: true}|{ok: false, reason: string}}
 */
export function validateLeanPin(archetypeCodeId, adjustmentId, version) {
  if (typeof adjustmentId !== 'string' || !adjustmentId || typeof version !== 'number') {
    return { ok: false, reason: LEAN_INVALIDATION_REASONS.MALFORMED };
  }
  if (!isValidAdjustmentId(archetypeCodeId, adjustmentId)) {
    return { ok: false, reason: LEAN_INVALIDATION_REASONS.NOT_IN_MENU };
  }
  if (version !== getCanonicalTextVersion(archetypeCodeId, adjustmentId)) {
    return { ok: false, reason: LEAN_INVALIDATION_REASONS.DEPRECATED_VERSION };
  }
  return { ok: true };
}

/**
 * Revalidates the FULL equip-time invariant set, not just per-pin validity:
 * menu membership + version currency (via validateLeanPin), then the at-rest
 * SET checks — conflict-group exclusion and the cap. The set checks exist
 * because the equip-time gate is not sufficient over time: conflict groups
 * are adjudication-gated and WILL change after leans were legally equipped,
 * so the last gate before the prompt must re-assert "never both sides of a
 * contradiction" (spec changelog #8) itself. Deterministic loser on a
 * conflict/cap breach: the LATER-equipped lean is omitted (missing
 * equippedAt loses); "data kept" as everywhere — omission + record only.
 * [Extends the spec Phase-1 item-7 check list (menu + currency) — flagged
 * for founder ratification in the Phase-1 report.]
 *
 * @param {Object} p
 * @param {Array<{adjustmentId: string, version: number, equippedAt?: string}>} p.standingLeans
 *   agent.standingLeans (ids-at-rest).
 * @param {string} p.archetypeCodeId the archetype about to be snapshotted.
 * @returns {{
 *   valid: Array<{adjustmentId: string, version: number, text: string}>,
 *   invalidated: Array<{adjustmentId: string|null, version: number|null, reason: string}>,
 * }}
 */
export function revalidateStandingLeans({ standingLeans = [], archetypeCodeId, leanCap } = {}) {
  // Default resolves per call (never at module load): the ONE cap source for
  // every legacy two-field caller — fenced prompt path, client display,
  // change-archetype rider — and the snapshot path alike (§9). An EXPLICIT
  // injection is validated against the closed vocabulary (ruling M6):
  // out-of-vocabulary → baseline + loud log, never a wider cap.
  let effectiveCap;
  if (leanCap === undefined) {
    effectiveCap = resolveLeanCap();
  } else if (isLegalLeanCap(leanCap)) {
    effectiveCap = leanCap;
  } else {
    console.error('[leanRevalidation] REJECTED out-of-vocabulary leanCap injection', JSON.stringify({
      injected: typeof leanCap === 'number' || typeof leanCap === 'boolean' ? leanCap : String(leanCap).slice(0, MAX_INVALIDATED_ID_CHARS),
      injectedType: typeof leanCap,
      fallback: STANDING_LEANS_CAP,
      archetypeCodeId: boundId(archetypeCodeId),
    }));
    effectiveCap = STANDING_LEANS_CAP;
  }
  const invalidated = [];

  // Pass 1 — per-pin validity through the shared rule, plus same-id dedupe
  // (first occurrence wins; conflict groups self-exclude same-id, so without
  // this a duplicate at-rest pin would pass every later check twice).
  const pinValid = [];
  const seenIds = new Set();
  for (const lean of Array.isArray(standingLeans) ? standingLeans : []) {
    const verdict = validateLeanPin(archetypeCodeId, lean?.adjustmentId, lean?.version);
    if (!verdict.ok) {
      invalidated.push({
        adjustmentId: boundId(lean?.adjustmentId),
        version: typeof lean?.version === 'number' ? lean.version : null,
        reason: verdict.reason,
      });
      continue;
    }
    if (seenIds.has(lean.adjustmentId)) {
      invalidated.push({
        adjustmentId: lean.adjustmentId,
        version: lean.version,
        reason: LEAN_INVALIDATION_REASONS.DUPLICATE_PIN,
      });
      continue;
    }
    seenIds.add(lean.adjustmentId);
    pinValid.push(lean);
  }

  // Pass 2 — at-rest set checks in deterministic equip order (earlier
  // equippedAt wins; ISO strings compare lexicographically BY CODE POINT —
  // not localeCompare, whose collation can move punctuation before digits;
  // a missing stamp sorts as '~' (after all digits) so it loses ties).
  const sortKey = (l) => String(l.equippedAt ?? '~');
  const ordered = [...pinValid].sort((a, b) => {
    const ka = sortKey(a);
    const kb = sortKey(b);
    return ka < kb ? -1 : ka > kb ? 1 : 0;
  });
  const accepted = [];
  for (const lean of ordered) {
    const conflicts = findEquipConflicts(
      archetypeCodeId,
      lean.adjustmentId,
      accepted.map((l) => l.adjustmentId),
    );
    if (conflicts.length > 0) {
      invalidated.push({
        adjustmentId: lean.adjustmentId,
        version: lean.version,
        reason: LEAN_INVALIDATION_REASONS.CONFLICTING_LEAN,
      });
      continue;
    }
    if (accepted.length >= effectiveCap) {
      invalidated.push({
        adjustmentId: lean.adjustmentId,
        version: lean.version,
        reason: LEAN_INVALIDATION_REASONS.OVER_CAP,
      });
      continue;
    }
    accepted.push(lean);
  }

  // Snapshot shape (master spec §3.1): id + version + RESOLVED CURRENT text,
  // in the original equip order for prompt stability.
  const acceptedIds = new Set(accepted.map((l) => l.adjustmentId));
  const valid = pinValid
    .filter((l) => acceptedIds.has(l.adjustmentId))
    .map((lean) => ({
      adjustmentId: lean.adjustmentId,
      version: lean.version,
      text: getCanonicalText(archetypeCodeId, lean.adjustmentId),
    }));
  return { valid, invalidated: boundInvalidated(invalidated) };
}

/**
 * The Release-2 customization-snapshot builder (fenced site 1 delegates to
 * THIS non-fenced function, so future logic tweaks — log payload, new
 * invalidation reasons, the settingsRev default — are ordinary changes, not
 * fence re-authorizations). Returns the four additive agentContext keys;
 * emits the [LeanRevalidation] event line when pins were omitted.
 *
 * @param {Object} agentData the full agent doc createAgentBattle received
 * @param {string} now       the creation timestamp (caller-owned)
 */
export function buildCustomizationSnapshot(agentData, now) {
  const { valid, invalidated } = revalidateStandingLeans({
    standingLeans: agentData.standingLeans,
    archetypeCodeId: agentData.archetype,
    // Mastery P2 dual anchor (kernel half): the shared per-call default —
    // structural max under enforcement, baseline (byte-identical) while
    // off — the same cap every other kernel caller resolves (§9).
  });
  if (invalidated.length > 0) {
    console.log('[LeanRevalidation]', JSON.stringify({
      agentId: agentData.id ?? null,
      archetype: agentData.archetype ?? null,
      invalidated,
      at: now,
    }));
  }
  return {
    standingLeans: valid,
    standingLeansInvalidated: invalidated,
    // BOUNDED like the invalidated records above (/code-review Phase-5):
    // agent.dials is owner-writable via the client SDK, so only a SHORT
    // STRING may enter the battle doc — a 1 MiB garbage value must never be
    // able to break battle creation. Sliced, not validated-against-the-menu:
    // an unknown-but-short desired value stays VISIBLE and fails closed at
    // the clamp (unknown_tempo_value), preserving desired-vs-effective.
    dials: typeof agentData.dials?.tempo === 'string' && agentData.dials.tempo
      ? { tempo: agentData.dials.tempo.slice(0, MAX_INVALIDATED_ID_CHARS) }
      : null,
    settingsRev: typeof agentData.settingsRev === 'number' ? agentData.settingsRev : 0,
  };
}
